<template>
  <div class="table-view-background" @click.self="$emit('close', $event)">
    <div class="table-view">
      <div class="table-header smooth-shadow">
        <icon size="18" class="mar-right-10">table_chart</icon>
        <h4>Table View</h4>

        <div class="search-box-wrapper">
          <input
            type="text"
            class="search-text-box"
            placeholder="search entity ids"
            v-model="searchTerm"
          />
          <i class="material-icons md-18">search</i>
        </div>

        <Chits
          :chits="getFilterChits"
          @clearFilter="(filter) => handleFilterChange({ filter })"
        />
        <div class="button-area">
          <!-- <button class="ghost-button">
            <icon size="18">print</icon>
            Print Table
          </button>

          <button class="ghost-button">
            <icon size="18">get_app</icon>
            Download Data
          </button> -->
          <button
            v-if="getStaticEntityFilters"
            :class="['toggle-button', { active: showFilters }]"
            @click="showFilters = !showFilters"
          >
            <icon size="18" @click="showFilters = !showFilters"
              >filter_list</icon
            >
            Filters
          </button>
          <ManageColumns :headers="getHeaders" v-model="selectedHeaders" />
        </div>
        <icon size="18" type="button" @click="$emit('close', $event)"
          >clear</icon
        >
      </div>
      <div class="table-container">
        <Table
          :headers="selectedHeaders"
          :rows="getData"
          :showFilters="showFilters"
          @onFilterChange="handleFilterChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Table from "@/components/static_entity_analysis/views/Table";
import ManageColumns from "@/components/static_entity_analysis/views/ManageColumns";
import { mapGetters, mapActions } from "vuex";
import Chits from "@/components/static_entity_analysis/views/chits";

export default {
  components: {
    Table,
    ManageColumns,
    Chits,
  },
  data() {
    return {
      showFilters: false,
      selectedHeaders: [],
      searchTerm: "",
    };
  },
  computed: {
    ...mapGetters("StaticEntity", [
      "getStaticEntities",
      "getStaticEntityFilters",
      "getAppliedFilters",
      "getStaticEntityMetaKeys",
    ]),
    ...mapGetters("Layers", ["getLayers"]),
    getHeaders() {
      let filterHeads = [];
      if (this.getStaticEntityMetaKeys) {
        // check if the data items is present in the available filters list
        filterHeads = this.getStaticEntityMetaKeys.map((columnId) => {
          // get the applied filters for this column
          let head = { id: columnId, text: columnId.split("__")[1] };

          // If static entity filters are there, set the filters key in header:
          if (this.getStaticEntityFilters) {
            // Get the applied filters:
            const appliedHeadFilters =
              this.getAppliedFilters && this.getAppliedFilters[columnId];

            let _filters = this.getStaticEntityFilters[columnId].map(
              (item) => ({
                id: item,
                name: item,
                column: columnId,
                value: item,
                // check if the column has applied filters or not
                isActive: appliedHeadFilters
                  ? appliedHeadFilters.includes(item)
                  : false,
              })
            );
            head = Object.assign(head, { filters: _filters });
          }
          return head;
        });
      }
      return [
        {
          text: "id",
          id: "id",
        },
        ...filterHeads,
        ...this.getLayers.map((layer) => ({
          id: layer.id,
          text: layer.name,
          format: true,
        })),
      ];
    },
    getData() {
      let data = [];
      this.getStaticEntities.forEach((entity) => {
        let row = { id: entity.id, ...entity.meta };
        this.getLayers.forEach((layer) => {
          if (entity.analyses && entity.analyses[layer.id])
            row[layer.id] = entity.analyses[layer.id].data;
          else row[layer.id] = "-";
        });
        data.push(row);
      });
      const regex = new RegExp(this.searchTerm, "i");
      return data.filter((item) => regex.test(item.id));
    },
    getFilterChits() {
      return Object.keys(this.getAppliedFilters).reduce(
        (result, filter_key) => {
          if (
            this.getAppliedFilters[filter_key] &&
            this.getAppliedFilters[filter_key].length > 0
          ) {
            const filter_name = filter_key.split("__")[1];
            const filters = this.getAppliedFilters[filter_key].map((item) => {
              return {
                id: item,
                name: item,
                value: item,
                column: filter_key,
                col_name: filter_name,
              };
            });
            result.push(...filters);
          }
          return result;
        },
        []
      );
    },
  },
  methods: {
    ...mapActions("StaticEntity", [
      "setStaticEntityFilters",
      "fetchStaticEntityFilters",
      "toggleEntityFilter",
    ]),
    handleFilterChange({ filter, heading }) {
      this.toggleEntityFilter({ column: filter.column, value: filter.value });
    },
  },
  watch: {
    getHeaders(value) {
      this.selectedHeaders = value;
    },
  },
  mounted() {
    this.fetchStaticEntityFilters();
  },
};
</script>

<style lang="scss" scoped>
.table-view-background {
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 99;
  background: var(--popup-background-color);
  display: flex;

  .table-view {
    width: 90%;
    max-width: 1400px;
    margin: 15px auto 15px auto;
    align-self: stretch;
    background: var(--color-default-background);
    border-radius: 5px 5px 0 0;
    display: flex;
    flex-direction: column;

    .table-header {
      background: var(--color-secondary);
      padding: 10px 10px 10px 15px;
      display: flex;
      flex-direction: row;
      align-items: center;
      border-radius: 5px 5px 0 0;

      .search-box-wrapper {
        position: relative;
        margin-right: 10px;

        .search-text-box {
          margin-left: 10px;
          padding-left: 40px;
        }

        i {
          position: absolute;
          padding: 12px;
          left: 10px;
        }
      }

      .filters {
        margin-left: 10px;
        display: flex;
        align-items: center;
        flex-direction: row;
        flex-wrap: wrap;

        span.chit {
          font-size: 0.9em;
          background-color: var(--color-primary-light);
          padding: 3px 10px;
          border-radius: 3px;
          color: var(--color-primary);
        }
      }

      .button-area {
        display: flex;
        flex-direction: row;
        align-items: center;

        .divider {
          width: 1px;
          align-self: stretch;
          background: var(--border-color);
        }

        .toggle-button {
          margin-right: 5px;

          /* &:first-child {
            margin-right: 5px;
          } */
        }

        .ghost-button {
          border: 0;
          padding: 10px 15px;
          color: var(--font-color);
          border-radius: 5px;
          outline: none;
          transition: 0.3s all ease-in-out;
          white-space: nowrap;
          display: flex;
          justify-content: center;
          align-items: center;
          background: var(--color-transparent);

          i {
            margin: 0 5px 0 0;
          }

          &:hover {
            cursor: pointer;
            background: var(--color-secondary-light);
          }

          &.active {
            color: var(--color-primary);
            &:hover {
              background: var(--color-primary-light);
            }
          }

          &:active {
            transform: scale(0.95);
          }
        }
      }
    }

    .table-container {
      flex: 1;
      min-height: 5px;
      /* background: gray; */
    }
  }
}
</style>
